<script lang="ts">
  import LL from '$i18n/i18n-svelte';
  import { getRankTierByQueue } from '$lib/er/rank-tiers/calculation';
  import RankTierImage from '$lib/er/rank-tiers/rank-tier-image.svelte';
  import RankTierText from '$lib/er/rank-tiers/rank-tier-text.svelte';
  import { getSeasonName } from '$lib/er/season/season';
  import type { ResultOf } from '$lib/erar/api';
  import type ERAR from '$lib/erar/spec';
  import { trimTrailingZero } from '$lib/utils/number';
  import type { LayoutData } from './$types';
  import MMRGraph from './mmr-graph.svelte';

  export let queue: NonNullable<LayoutData['player']>['queues'][number];
  export let statistics: ResultOf<'GET /player/[id]/statistics'>;
  export let history: ERAR.Schema.MMRHistory;

  $: rankTier = getRankTierByQueue(queue);
  $: averageKill = statistics.overall.kda[0] / statistics.overall.plays;
  $: averageDeath = statistics.overall.kda[1] / statistics.overall.plays;
  $: averageAssist = statistics.overall.kda[2] / statistics.overall.plays;

  const COUNT = 8;
  const TICK = 2;
  const LENGTH = COUNT * TICK + 1;
  let historyWithParsedDate = Object.fromEntries(
    Object.entries(history)
      .slice(-LENGTH)
      .map(([k, v]) => [parseDate(k), v.end]),
  );
  let dates = Object.keys(historyWithParsedDate);
  let lastDate = parseInt(dates.at(-1)!);

  function parseDate(v: string): number {
    const date = new Date(0);
    date.setUTCFullYear(2000 + parseInt(v.substring(0, 2)));
    date.setUTCMonth(parseInt(v.substring(2, 4)) - 1);
    date.setUTCDate(parseInt(v.substring(4, 6)));
    return date.getTime();
  }
  function getMMRHistory() {
    const startDate = lastDate - 86400000 * COUNT * TICK;
    let date = parseInt(dates[0]);
    let mmr = Object.values(historyWithParsedDate)[0];
    while (date < startDate) {
      if (date in historyWithParsedDate) {
        mmr = historyWithParsedDate[date];
      }
      date += 86400000;
    }
    const R = [];
    while (date <= lastDate) {
      const value = historyWithParsedDate[date];
      if (value !== undefined) {
        mmr = value;
      }
      date += 86400000;
      R.push(mmr);
    }
    return [...Array(LENGTH - R.length).fill(R[0]), ...R];
  }
</script>

<section class="panel" data-tier={rankTier.tier.tierName.toLowerCase()}>
  <div class="tier">
    <RankTierImage type="transparent" rankTierName={rankTier.tier.tierName} height="150" />
    <div>
      <span class="season-name">{getSeasonName($LL, queue.seasonId)}</span>
      <RankTierText {rankTier} seasonId={queue.seasonId} hideIcon />
    </div>
  </div>
  {#if statistics.overall.plays === 0}
    <span class="noplay">플레이 기록 없음</span>
  {:else}
    <div class="stats">
      <div class="stat kda">
        <h3>평균 K·D·A</h3>
        <span>{trimTrailingZero(averageKill, 1)}</span>
        <span>{trimTrailingZero(averageDeath, 1)}</span>
        <span>{trimTrailingZero(averageAssist, 1)}</span>
      </div>
      <div class="stat">
        <h3>승률</h3>
        <span>{trimTrailingZero((queue.totalWins / queue.totalPlays) * 100, 1)}%</span>
      </div>
      <div class="stat">
        <h3>반타작률</h3>
        <span>{trimTrailingZero(statistics.overall.halfRate * 100, 1)}%</span>
      </div>
      <div class="stat">
        <h3>게임 수</h3>
        <span>{queue.totalPlays}</span>
      </div>
      <div class="stat">
        <h3>승리 수</h3>
        <span>{queue.totalWins}</span>
      </div>
      <div class="stat">
        <h3>플레이 시간</h3>
        <span>{trimTrailingZero(statistics.overall.playTime / 60 / 60, 1)}시간</span>
      </div>
    </div>
    <div class="graph">
      <MMRGraph
        history={getMMRHistory()}
        count={COUNT}
        tick={TICK}
        {lastDate}
        seasonId={queue.seasonId}
      />
    </div>
  {/if}
</section>

<style lang="postcss">
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: stretch;

    background: radial-gradient(
        circle at top left,
        rgba(var(---tier-color), 0.5) 0%,
        rgba(var(---tier-color), 0) 80%
      ),
      #191719;

    :global(.rp-tier-text) {
      font-size: var(--size-6);

      :global(.rank-tier-image) {
        height: 2rem;
      }
    }

    &[data-tier='unranked'] {
      ---tier-color: var(--color-tier-unranked-rgb);
    }
    &[data-tier='iron'] {
      ---tier-color: var(--color-tier-iron-rgb);
    }
    &[data-tier='bronze'] {
      ---tier-color: var(--color-tier-bronze-rgb);
    }
    &[data-tier='silver'] {
      ---tier-color: var(--color-tier-silver-rgb);
    }
    &[data-tier='gold'] {
      ---tier-color: var(--color-tier-gold-rgb);
    }
    &[data-tier='platinum'] {
      ---tier-color: var(--color-tier-platinum-rgb);
    }
    &[data-tier='diamond'] {
      ---tier-color: var(--color-tier-diamond-rgb);
    }
    &[data-tier='meteorite'] {
      ---tier-color: var(--color-tier-meteorite-rgb);
    }
    &[data-tier='mithril'] {
      ---tier-color: var(--color-tier-mithril-rgb);
    }
    &[data-tier='demigod'] {
      ---tier-color: var(--color-tier-demigod-rgb);
    }
    &[data-tier='eternity'] {
      ---tier-color: var(--color-tier-eternity-rgb);
    }
  }

  .tier {
    display: flex;
    gap: var(--space-5);
    align-items: center;
    color: rgba(255, 255, 255, 80%);
    :global(.rp-tier-text) {
      align-items: flex-start;
      flex-direction: column;
      line-height: 1.2;
      font-size: var(--size-8);
    }
    :global(.rp-tier-text .rp) {
      font-size: var(--size-10);
      font-weight: bold;
    }
  }

  .season-name {
    font-size: var(--size-5);
  }

  .noplay {
    font-size: var(--size-5);
    margin: 0 auto;
    margin-bottom: var(--space-4);
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(3, auto);
    gap: var(--space-5);
    margin: var(--space-4);
    margin-top: var(--space-1);
  }

  .stat {
    h3 {
      font-size: var(--size-5);
      font-weight: normal;
      color: rgba(255, 255, 255, 50%);
    }
    span {
      font-size: var(--size-6);
      font-weight: bold;
      color: rgba(255, 255, 255, 70%);
    }
  }

  .graph {
    display: flex;
    justify-content: center;
  }

  .kda {
    span:not(:last-child)::after {
      content: '/';
      /* color: var(--white-a10); */
      margin: 0 3px;
      font-size: var(--size-3);
      vertical-align: middle;
    }
  }
</style>
